/**
 * Scene Manager handles scene transitions and lifecycle
 * Manages the scene stack and provides smooth transitions between scenes
 */

import { Container } from 'pixi.js';
import { BaseScene, type SceneContext } from '@scenes/BaseScene';
import type { TransitionType } from './types';
import { eventBus } from './EventBus';

type SceneConstructor = new (context: SceneContext) => BaseScene;

interface TransitionConfig {
  duration: number;
  type: TransitionType;
}

const DEFAULT_TRANSITION: TransitionConfig = {
  duration: 300,
  type: 'fade',
};

export class SceneManager {
  private container: Container;
  private context: SceneContext;
  private currentScene: BaseScene | null = null;
  private sceneRegistry: Map<string, SceneConstructor> = new Map();
  private isTransitioning: boolean = false;

  constructor(container: Container, context: SceneContext) {
    this.container = container;
    this.context = context;
  }

  /**
   * Register a scene class with a name
   */
  register(name: string, SceneClass: SceneConstructor): void {
    this.sceneRegistry.set(name, SceneClass);
  }

  /**
   * Get the current active scene
   */
  getCurrentScene(): BaseScene | null {
    return this.currentScene;
  }

  /**
   * Check if currently transitioning between scenes
   */
  isInTransition(): boolean {
    return this.isTransitioning;
  }

  /**
   * Change to a new scene with transition
   */
  async changeTo(
    sceneName: string,
    transition?: Partial<TransitionConfig>
  ): Promise<void> {
    if (this.isTransitioning) {
      console.warn('Already transitioning, ignoring scene change request');
      return;
    }

    const SceneClass = this.sceneRegistry.get(sceneName);
    if (!SceneClass) {
      throw new Error(`Scene '${sceneName}' not found in registry`);
    }

    this.isTransitioning = true;

    const config: TransitionConfig = {
      ...DEFAULT_TRANSITION,
      ...transition,
    };

    // Create new scene
    const newScene = new SceneClass(this.context);

    try {
      // Initialize the new scene
      await newScene.init();

      // Transition out current scene
      if (this.currentScene) {
        await this.transitionOut(this.currentScene, config);
        this.currentScene.onExit();
        this.container.removeChild(this.currentScene);
        this.currentScene.destroy();
      }

      // Add and transition in new scene
      this.container.addChild(newScene);
      await this.transitionIn(newScene, config);
      newScene.onEnter();

      this.currentScene = newScene;
      eventBus.emit('scene:change', { sceneName });
    } catch (error) {
      console.error(`Failed to change to scene '${sceneName}':`, error);
      newScene.destroy();
      throw error;
    } finally {
      this.isTransitioning = false;
    }
  }

  /**
   * Perform transition out animation
   */
  private async transitionOut(
    scene: BaseScene,
    config: TransitionConfig
  ): Promise<void> {
    if (config.type === 'none' || config.duration === 0) {
      scene.alpha = 0;
      return;
    }

    return this.animateAlpha(scene, 1, 0, config.duration);
  }

  /**
   * Perform transition in animation
   */
  private async transitionIn(
    scene: BaseScene,
    config: TransitionConfig
  ): Promise<void> {
    if (config.type === 'none' || config.duration === 0) {
      scene.alpha = 1;
      return;
    }

    scene.alpha = 0;
    return this.animateAlpha(scene, 0, 1, config.duration);
  }

  /**
   * Animate alpha value
   */
  private animateAlpha(
    target: Container,
    from: number,
    to: number,
    duration: number
  ): Promise<void> {
    return new Promise((resolve) => {
      const startTime = performance.now();
      target.alpha = from;

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out quad
        const eased = 1 - (1 - progress) * (1 - progress);
        target.alpha = from + (to - from) * eased;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          target.alpha = to;
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  /**
   * Update the current scene
   */
  update(delta: number): void {
    if (this.currentScene && !this.isTransitioning) {
      this.currentScene.update(delta);
    }
  }

  /**
   * Handle window resize
   */
  onResize(width: number, height: number): void {
    this.context.width = width;
    this.context.height = height;
    if (this.currentScene) {
      this.currentScene.onResize(width, height);
    }
  }

  /**
   * Pause current scene
   */
  pause(): void {
    if (this.currentScene) {
      this.currentScene.pause();
      eventBus.emit('session:pause');
    }
  }

  /**
   * Resume current scene
   */
  resume(): void {
    if (this.currentScene) {
      this.currentScene.resume();
      eventBus.emit('session:resume');
    }
  }

  /**
   * Clean up all scenes
   */
  destroy(): void {
    if (this.currentScene) {
      this.currentScene.destroy();
      this.currentScene = null;
    }
    this.sceneRegistry.clear();
  }
}
